import { Link, useLocation } from "@tanstack/react-router";
import CollectionListItem from './CollectionListItem';
import ITag from '../ITag';
import { useEffect, useRef, useState, useMemo } from "react";
import { getCollections } from "../../lib/api";
import chainData from "../../chains";

function buildChainSlugMap() {
    const entries = chainData
        .filter((entry: any) => entry && entry.chainId != null)
        .map((entry: any) => {
            const chainId = String(entry.chainId);
            const raw = entry.chainName || entry.network || entry.tokName || "";
            const slug = raw
                .toString()
                .trim()
                .toLowerCase()
                .replace(/[^a-z0-9-]+/g, "-")
                .replace(/^-+|-+$/g, "");
            return [chainId, slug];
        });
    return Object.fromEntries(entries);
}

const chainSlugMap: Record<string, string> = buildChainSlugMap();

function toSlug(value: string): string {
    return value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9-]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

function resolveCollectionPath(item: any, locale: string): string {
    const chainEntry = chainData.find((entry: any) => entry && String(entry.chainId) === String(item.chainId ?? item.network));
    const chain = toSlug(chainEntry?.chainName || chainEntry?.network || String(item.chainId ?? item.network));
    const slug = item.canonicalName || item.slug || String(item.id);
    const localeSegment = locale ? `/${locale}` : "";
    return `${localeSegment}/collections/${chain}/${slug}`;
}

function SecCollectionList({ rerender, searchParam, net, initialCollections, initialPagination }) {
    const location = useLocation();
    const [page, setPage] = useState(initialPagination?.page || 1);
    // Seed from SSR loader data on first render; track if we've applied it
    const [collections, setCollections] = useState<any[]>(() => {
        // Only use initial data if it's provided and matches current net/page/search params
        if (initialCollections && initialCollections.length > 0) {
            return initialCollections;
        }
        return [];
    });
    const [pagination, setPagination] = useState(initialPagination || { hasNext: false, hasPrev: false, page: 1, totalPages: 0 });
    const topRef = useRef<HTMLDivElement | null>(null);

    const PAGE_SIZE = 24;

    const currentLocale = useMemo(() => {
        const pathLocale = typeof location.pathname === "string"
            ? location.pathname.split("/").filter(Boolean)[0]
            : "";
        return pathLocale || "";
    }, [location.pathname]);

    const executeScroll = () => topRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' });

    // Track which searchParam/net combo we already have data for.
    // Skip fetch on mount (loader data covers it); only refetch when
    // searchParam or net actually changes client-side.
    const hasRun = useRef(false);
    useEffect(() => {
        if (!hasRun.current) {
            hasRun.current = true;
            return; // SSR loader covered initial load; never refetch on mount
        }

        // ONLY refetch when search params or network change client-side
        let params: any = { page, limit: PAGE_SIZE, sparam: searchParam || '' };
        if (net !== "any") params.net = net;

        getCollections(params).then((result) => {
            setCollections(result.data || []);
            setPagination(result.pagination || { hasNext: false, hasPrev: false, page: 1, totalPages: 0 });
        }).catch(err => {
            console.error('Failed to fetch collections:', err);
            setCollections([]);
            setPagination({ hasNext: false, hasPrev: false, page: 1, totalPages: 0 });
        });
    }, [searchParam, net, page]);

    return (
        <div className="px-2 pb-3" ref={topRef}>
            <div className="row justify-content-center g-3">
                {collections.map((item, i) => (
                    <CollectionListItem
                        key={i}
                        rerender={rerender}
                        path1={resolveCollectionPath(item, currentLocale)}
                        img1={item.imageUrl}
                        text1={item.name}
                        text2={item.symbol}
                        price={item.price}
                        isCached={item.isCached}
                        full={item}
                    />
                ))}
                <div id="nav1" className="col-md-12 text-center d-flex justify-content-center align-items-center gap-2 pt-2">
                    {pagination.hasPrev && (
                        <Link className="btn bg-gradient-dark mb-0" onClick={() => { setPage(p => p - 1); executeScroll(); }} to="#">
                            <ITag ClassName="fas fa-chevron-left mr-10" />Previous
                        </Link>
                    )}
                    {pagination.totalPages > 0 && (
                        <span className="text-muted mx-2">
                            Page {pagination.page} of {pagination.totalPages}
                        </span>
                    )}
                    {pagination.hasNext && (
                        <Link className="btn bg-gradient-dark mb-0" onClick={() => { setPage(p => p + 1); executeScroll(); }} to="#">
                            Next<ITag ClassName="fas fa-chevron-right ml-10" />
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
}

export default SecCollectionList;
